import { Link } from "react-router-dom";

import type { ProductListItem } from "../api/types";
import { useCan } from "../auth/AuthProvider";
import { CAP } from "../auth/capabilities";
import { FAMILY_LABELS, familyForCategory } from "../catalog/taxonomy";
import { formatMoney } from "../components/ui";
import { discountLabel, isDiscounted } from "../products/money";
import { OperatorStatus } from "../products/OperatorStatus";

/**
 * What the shop is currently discounting, and what it would take to change it.
 *
 * Every number here is the WORKING price — the revision this listing reports,
 * which is the live one for a published product and the draft for one that has
 * never been published. A product with unpublished changes says so beside its
 * lifecycle rather than letting the row imply the draft price is in force; the
 * listing carries one revision's prices, and inventing a second column by
 * fetching each product's detail would be a request per row.
 *
 * The discount percentage is derived for display and is never sent anywhere.
 */
export function DiscountsTable({
  items,
  onSetDiscount,
  onRemoveDiscount,
}: {
  items: ProductListItem[];
  onSetDiscount: (item: ProductListItem) => void;
  onRemoveDiscount: (item: ProductListItem) => void;
}) {
  const canWrite = useCan(CAP.productsWrite);

  return (
    <div className="tableWrap">
      <table className="table discounts__table">
        <thead>
          <tr>
            <th scope="col">Product</th>
            <th scope="col">Family</th>
            <th scope="col">Base price</th>
            <th scope="col">Sale price</th>
            <th scope="col">Discount</th>
            <th scope="col">Lifecycle</th>
            <th scope="col">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const family = familyForCategory(item.category);
            const discounted = isDiscounted(item.base_price, item.sale_price);
            const label = discountLabel(item.base_price, item.sale_price);

            // Read from the row, never computed: the backend refuses every
            // write on an archived product, and this renders that fact.
            const archived = item.status === "archived";
            // The prices in this row are the WORKING revision, so where an
            // open draft exists the row must say the shopper is not seeing
            // them yet.
            //
            // Derived from the draft's presence and the product's lifecycle
            // rather than from `active_revision_number`: under the working
            // scope that field is null whenever a draft is being reported, so
            // it can no longer answer "is there something live behind this".
            // `status` can — a product is only ever `draft` before its first
            // publication, and publishing moves it to `published` for good —
            // so a draft on anything else is work the shopper has not been
            // shown. A never-published product is NOT flagged: its lifecycle
            // already says it is not live, and there is nothing behind it.
            const unpublished =
              item.draft_revision_number !== null && item.status !== "draft";

            return (
              <tr key={item.product_id}>
                <td>
                  <span className="discounts__name">{item.name}</span>
                  {item.product_code ? (
                    <span className="discounts__code">{item.product_code}</span>
                  ) : (
                    <span className="discounts__code discounts__code--missing">
                      Code missing
                    </span>
                  )}
                </td>

                <td>
                  {family ? (
                    FAMILY_LABELS[family]
                  ) : (
                    <span className="muted">Not classified</span>
                  )}
                </td>

                <td className="num">
                  {item.base_price === null || item.currency === null ? (
                    <span className="muted">—</span>
                  ) : (
                    formatMoney(item.base_price, item.currency)
                  )}
                </td>

                <td className="num">
                  {discounted && item.currency !== null ? (
                    formatMoney(item.sale_price as number, item.currency)
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>

                <td className="num">
                  {label === null ? (
                    <span className="muted">—</span>
                  ) : (
                    <span className="tag tag--sale">{label} off</span>
                  )}
                </td>

                <td>
                  <span className="discounts__lifecycle">
                    <OperatorStatus status={item.status} />
                    {unpublished ? (
                      <span className="state state--warn">
                        Unpublished changes
                      </span>
                    ) : null}
                  </span>
                </td>

                <td>
                  <span className="discounts__actions">
                    {/* A discount is a sale price on the product's working
                        revision, so setting or clearing one is a product write.
                        An administrator who may only READ the catalogue sees
                        what things cost and no way to change it. */}
                    {archived || !canWrite ? (
                      <span className="muted discounts__readonly">
                        Read-only
                      </span>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn--sm"
                          onClick={() => onSetDiscount(item)}
                        >
                          {discounted ? "Change" : "Set discount"}
                        </button>
                        {discounted ? (
                          <button
                            type="button"
                            className="btn btn--sm"
                            onClick={() => onRemoveDiscount(item)}
                          >
                            Remove
                          </button>
                        ) : null}
                      </>
                    )}
                    <Link
                      className="btn btn--sm btn--subtle"
                      to={`/products/${item.product_id}`}
                    >
                      Open
                    </Link>
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
